import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  getAdminHotspotChannels,
  getAdminHotspotSources,
  createAdminHotspotChannel,
  updateAdminHotspotChannel,
  deleteAdminHotspotChannel,
  type HotspotChannel,
  type HotspotChannelSource,
  type HotspotTemplate,
  type HotspotSourceItem,
} from '@/api/client';

const domainOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '热点源';
  }
};

const parseSourcesText = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, url] = line.includes('|') ? line.split('|') : ['', line];
      const u = url.trim();
      return {
        name: name.trim() || domainOf(u),
        url: u,
        type: u.includes('1201') ? 'dailyhot' : u.includes('top.baidu.com') ? 'html' : u.includes('bilibili.com') ? 'json' : 'rss',
      };
    });

const splitKeywords = (text: string) =>
  text
    .split(/[,，、\s]+/)
    .map((k) => k.trim())
    .filter(Boolean);

export function HotspotAdminTab() {
  const [channels, setChannels] = React.useState<HotspotChannel[]>([]);
  const [templates, setTemplates] = React.useState<Record<string, HotspotTemplate[]>>({});
  const [sourceCatalog, setSourceCatalog] = React.useState<HotspotSourceItem[]>([]);
  const [status, setStatus] = React.useState('');

  // 编辑/新建表单
  const [editing, setEditing] = React.useState<HotspotChannel | null | 'new'>(null);
  const [formName, setFormName] = React.useState('');
  const [formSourcesText, setFormSourcesText] = React.useState('');
  const [formKeywords, setFormKeywords] = React.useState('');
  const [formBlacklist, setFormBlacklist] = React.useState('');
  const [formEnabled, setFormEnabled] = React.useState(true);
  const [formError, setFormError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const [chData, srcData] = await Promise.all([getAdminHotspotChannels(), getAdminHotspotSources()]);
      setChannels(chData.items || []);
      setTemplates(chData.templates || {});
      setSourceCatalog(srcData.items || []);
      setStatus('');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : '加载失败');
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setEditing('new');
    setFormName('');
    setFormSourcesText('');
    setFormKeywords('');
    setFormBlacklist('');
    setFormEnabled(true);
    setFormError('');
  };

  const startEdit = (ch: HotspotChannel) => {
    setEditing(ch);
    setFormName(ch.name);
    setFormSourcesText(ch.sources.map((s) => (s.name ? `${s.name}|${s.url}` : s.url)).join('\n'));
    setFormKeywords((ch.keywords || []).join('，'));
    setFormBlacklist((ch.blacklist || []).join('，'));
    setFormEnabled(ch.enabled);
    setFormError('');
  };

  const save = async () => {
    const name = formName.trim();
    if (!name) {
      setFormError('赛道名称不能为空');
      return;
    }
    const sources = parseSourcesText(formSourcesText);
    if (sources.length === 0) {
      setFormError('至少填写一个热点源（每行「名称|URL」或裸 URL）');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name,
        sources,
        keywords: splitKeywords(formKeywords),
        blacklist: splitKeywords(formBlacklist),
        enabled: formEnabled,
      };
      if (editing === 'new') {
        await createAdminHotspotChannel(payload);
      } else if (editing) {
        await updateAdminHotspotChannel(editing.id, payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ch: HotspotChannel) => {
    if (!window.confirm(`删除赛道「${ch.name}」？用户将不再看到该赛道。`)) return;
    try {
      await deleteAdminHotspotChannel(ch.id);
      if (editing && editing !== 'new' && editing.id === ch.id) setEditing(null);
      await load();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : '删除失败');
    }
  };

  const toggleEnabled = async (ch: HotspotChannel) => {
    try {
      await updateAdminHotspotChannel(ch.id, {
        name: ch.name,
        sources: ch.sources,
        keywords: ch.keywords,
        blacklist: ch.blacklist,
        enabled: !ch.enabled,
      });
      await load();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : '切换失败');
    }
  };

  const appendTemplate = (tpl: HotspotTemplate) => {
    const lines = (tpl.sources || []).map((s: HotspotChannelSource) => `${s.name}|${s.url}`).join('\n');
    setEditing('new');
    setFormName(tpl.name);
    setFormSourcesText(lines);
    setFormKeywords((tpl.keywords || []).join('，'));
    setFormBlacklist('');
    setFormEnabled(true);
    setFormError('');
  };

  const appendCatalogSource = (src: HotspotSourceItem) => {
    setFormSourcesText((prev) => {
      const lines = prev.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.some((l) => l.includes(src.url))) return prev;
      return [...lines, `${src.name}|${src.url}`].join('\n');
    });
    setFormError('');
  };

  const sourceGroups = React.useMemo(() => {
    const g: Record<string, HotspotSourceItem[]> = {};
    for (const s of sourceCatalog) {
      (g[s.group] = g[s.group] || []).push(s);
    }
    return g;
  }, [sourceCatalog]);

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          管理全局热点赛道与数据源。赛道配置对所有用户可见；用户在「热点」页面只读浏览。
        </p>
        <Button size="sm" className="h-7 text-xs" onClick={startNew}>
          新建赛道
        </Button>
      </div>
      {status && <p className="text-xs text-destructive">{status}</p>}

      {editing !== null && (
        <div className="space-y-3 rounded border p-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">{editing === 'new' ? '新建赛道' : `编辑赛道：${editing.name}`}</span>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditing(null)} disabled={saving}>
                取消
              </Button>
              <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving}>
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
          {formError && <p className="text-xs text-destructive">{formError}</p>}
          <div className="grid grid-cols-3 gap-2">
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">赛道名称</span>
              <Input className="h-7" value={formName} onChange={(e) => setFormName(e.target.value)} maxLength={30} />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">关键词白名单（逗号分隔，留空=全部）</span>
              <Input className="h-7" value={formKeywords} onChange={(e) => setFormKeywords(e.target.value)} placeholder="如：高考，志愿，录取" />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">黑名单（逗号分隔）</span>
              <Input className="h-7" value={formBlacklist} onChange={(e) => setFormBlacklist(e.target.value)} placeholder="如：广告，软文" />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">
              热点源（每行「名称|URL」；RSS/Atom、百度热搜页、B站接口、DailyHotApi 自动识别）
            </span>
            <textarea
              className="h-28 w-full resize-none rounded border bg-transparent p-2 font-mono text-xs outline-none focus:ring-1"
              value={formSourcesText}
              onChange={(e) => setFormSourcesText(e.target.value)}
              placeholder={'量子位|https://www.qbitai.com/feed\n抖音热榜|http://127.0.0.1:1201/douyin'}
            />
          </label>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />
              启用（关闭后用户侧不显示该赛道）
            </label>
          </div>
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">快捷添加：预置模板（点击填充表单）</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(templates).map(([group, tpls]) => (
                <React.Fragment key={group}>
                  {tpls.map((t) => (
                    <button
                      key={t.name}
                      onClick={() => appendTemplate(t)}
                      className="rounded border border-border px-1.5 py-0.5 text-[11px] text-foreground hover:bg-accent/60"
                      title={`${group} · ${(t.keywords || []).join('、')}`}
                    >
                      {t.name}
                      {t.added ? ' ✓' : ''}
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="text-xs text-muted-foreground">可选源目录（点击追加到上方源列表）</div>
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {Object.entries(sourceGroups).map(([group, sources]) => (
                <div key={group}>
                  <div className="text-[10px] text-muted-foreground">{group}</div>
                  <div className="flex flex-wrap gap-1">
                    {sources.map((s) => (
                      <button
                        key={s.url}
                        onClick={() => appendCatalogSource(s)}
                        className="rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-accent/60"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded border">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b bg-muted/40 text-muted-foreground">
              <th className="px-2 py-1.5 font-medium">排序</th>
              <th className="px-2 py-1.5 font-medium">名称</th>
              <th className="px-2 py-1.5 font-medium">源</th>
              <th className="px-2 py-1.5 font-medium">关键词</th>
              <th className="px-2 py-1.5 font-medium">状态</th>
              <th className="px-2 py-1.5 font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((ch) => (
              <tr key={ch.id} className="border-b last:border-0 hover:bg-accent/30">
                <td className="px-2 py-1.5 tabular-nums">{ch.sortOrder + 1}</td>
                <td className="max-w-[140px] truncate px-2 py-1.5 font-medium">{ch.name}</td>
                <td className="px-2 py-1.5">
                  <div className="flex max-w-[260px] flex-wrap gap-0.5">
                    {ch.sources.map((s) => (
                      <Badge key={s.url} variant="outline" className="text-[9px]">
                        {s.name}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="max-w-[160px] truncate px-2 py-1.5 text-muted-foreground">
                  {(ch.keywords || []).join('、') || '—'}
                </td>
                <td className="px-2 py-1.5">
                  <button
                    onClick={() => toggleEnabled(ch)}
                    className={`rounded px-1.5 py-0.5 text-[10px] ${
                      ch.enabled ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'
                    }`}
                    title="点击切换启停"
                  >
                    {ch.enabled ? '启用' : '停用'}
                  </button>
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex gap-1">
                    <button className="rounded px-1.5 py-0.5 text-[10px] hover:bg-accent/60" onClick={() => startEdit(ch)}>
                      编辑
                    </button>
                    <button
                      className="rounded px-1.5 py-0.5 text-[10px] text-destructive hover:bg-accent/60"
                      onClick={() => remove(ch)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {channels.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">
                  暂无赛道，点击「新建赛道」或上方模板添加
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
